// Phase 3A — admin Journey & Conversion view.
// Shows measurable first-party facts only. Anything the data cannot answer is
// labelled "not measurable", never synthesized.
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { AdminSection } from "./admin";
import { computeFunnel, stepRates, type FunnelCount } from "@/lib/funnel";
import { computeReturnMetrics, resumeRate } from "@/lib/return-loop";

export const Route = createFileRoute("/_authenticated/admin/journey")({
  head: () => ({
    meta: [{ title: "Journey · Admin" }, { name: "robots", content: "noindex" }],
  }),
  component: AdminJourney,
});

type Row = { event: string; path: string | null; session_id: string | null };
type Win = "24h" | "7d" | "30d";
const RANGES: Record<Win, number> = { "24h": 1, "7d": 7, "30d": 30 };

function since(days: number) {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString();
}

function AdminJourney() {
  const [win, setWin] = useState<Win>("7d");
  const [rows, setRows] = useState<Row[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setRows(null);
    setErr(null);
    supabase
      .from("analytics_events")
      .select("event,path,session_id")
      .gte("created_at", since(RANGES[win]))
      .order("created_at", { ascending: false })
      .limit(10000)
      .then(({ data, error }) => {
        if (!alive) return;
        if (error) {
          setErr(error.message);
          setRows([]);
          return;
        }
        setRows((data ?? []) as Row[]);
      });
    return () => {
      alive = false;
    };
  }, [win]);

  const funnel: FunnelCount[] = computeFunnel(rows ?? []);
  const rates = stepRates(funnel);
  const returnMetrics = computeReturnMetrics(rows ?? []);
  const resume = resumeRate(returnMetrics);

  const entry = new Map<string, number>();
  const ctas = new Map<string, number>();
  (rows ?? []).forEach((r) => {
    if (r.event === "pageview" && r.path) entry.set(r.path, (entry.get(r.path) ?? 0) + 1);
    if (r.event.startsWith("journey_")) ctas.set(r.event, (ctas.get(r.event) ?? 0) + 1);
  });
  const topEntry = [...entry.entries()].sort((a, b) => b[1] - a[1]).slice(0, 12);
  const ctaRows = [...ctas.entries()].sort((a, b) => b[1] - a[1]);

  return (
    <AdminSection
      title="Journey & conversion"
      description="LISTEN → ENTER → OWN. First-party events only, recorded after consent, with no personal identifiers. Intent signals are never counted as conversions and correlation is never presented as causation."
      action={
        <div className="flex gap-1 rounded-full border hairline p-1">
          {(Object.keys(RANGES) as Win[]).map((w) => (
            <button
              key={w}
              type="button"
              onClick={() => setWin(w)}
              className={`rounded-full px-3 py-1 text-xs uppercase tracking-[0.2em] transition ${
                win === w ? "bg-gold-gradient font-semibold text-obsidian" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {w}
            </button>
          ))}
        </div>
      }
    >
      {err && (
        <p className="mb-6 rounded-lg border border-rose-500/30 bg-rose-500/10 p-4 text-sm text-rose-200">
          Couldn't load journey data: {err}
        </p>
      )}

      <div className="rounded-lg border hairline p-6">
        <h3 className="font-serif text-xl">Funnel</h3>
        <ul className="mt-4 divide-y hairline">
          {funnel.map((s, i) => {
            const rate = i > 0 ? rates[i - 1]?.rate ?? null : null;
            return (
              <li key={s.id} className="flex flex-wrap items-baseline justify-between gap-2 py-3">
                <div>
                  <p className="text-sm text-ivory">{s.label}</p>
                  <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                    {s.stage} · {s.signal === "completed" ? "completed conversion" : "intent signal"}
                  </p>
                  {s.caveat && <p className="mt-1 text-xs text-muted-foreground">{s.caveat}</p>}
                </div>
                <div className="text-right">
                  <p className="font-mono text-sm text-gold-luminous">
                    {rows === null ? "—" : `${s.sessions_count.toLocaleString()} sessions`}
                  </p>
                  <p className="font-mono text-xs text-muted-foreground">
                    {rows === null ? "—" : `${s.events_count.toLocaleString()} events`}
                    {rate !== null ? ` · ${(rate * 100).toFixed(1)}% of previous step` : ""}
                  </p>
                </div>
              </li>
            );
          })}
        </ul>
        <p className="mt-4 text-xs text-muted-foreground">
          Step-to-step percentages compare distinct sessions per step in the same window. They are not
          per-visitor cohort progression, and no attribution or causal claim is implied.
        </p>
      </div>

      <div className="mt-8 rounded-lg border hairline p-6">
        <h3 className="font-serif text-xl">Return loop</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          VISIT → EXPERIENCE → REMEMBER → RETURN → IDENTIFY → OWN. Each row states whether it is
          measured from first-party events, a declared proxy, or not measurable at all.
        </p>
        <ul className="mt-4 divide-y hairline">
          {returnMetrics.map((m) => (
            <li key={m.id} className="flex flex-wrap items-baseline justify-between gap-2 py-3">
              <div>
                <p className="text-sm text-ivory">{m.label}</p>
                <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                  {m.stage} · {m.availability}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">{m.note}</p>
              </div>
              <div className="text-right">
                <p className="font-mono text-sm text-gold-luminous">
                  {m.availability === "unavailable"
                    ? "not measurable"
                    : rows === null
                      ? "—"
                      : `${m.sessions_count.toLocaleString()} sessions`}
                </p>
                {m.availability !== "unavailable" && (
                  <p className="font-mono text-xs text-muted-foreground">
                    {rows === null ? "—" : `${m.events_count.toLocaleString()} events`}
                  </p>
                )}
              </div>
            </li>
          ))}
        </ul>
        <p className="mt-4 text-xs text-muted-foreground">
          Continuation resume rate:{" "}
          {rows === null ? "—" : resume === null ? "no continuations shown in this window" : `${(resume * 100).toFixed(1)}%`}
          . Ratio of continuation clicks to continuation impressions in the same window — not a
          per-visitor rate and not a causal claim.
        </p>
      </div>

      <div className="mt-8 grid gap-8 lg:grid-cols-2">
        <div className="rounded-lg border hairline p-6">
          <h3 className="font-serif text-xl">Entry pages</h3>
          {rows === null ? (
            <p className="mt-4 text-sm text-muted-foreground">Loading…</p>
          ) : topEntry.length === 0 ? (
            <p className="mt-4 text-sm text-muted-foreground">No pageviews in this window.</p>
          ) : (
            <ul className="mt-4 divide-y hairline">
              {topEntry.map(([p, n]) => (
                <li key={p} className="flex items-center justify-between gap-3 py-2">
                  <span className="truncate text-sm text-muted-foreground">{p}</span>
                  <span className="font-mono text-sm text-gold-luminous">{n.toLocaleString()}</span>
                </li>
              ))}
            </ul>
          )}
          <p className="mt-4 text-xs text-muted-foreground">
            First-hit attribution per session is not measurable with the current event schema; these are
            pageview totals by path.
          </p>
        </div>

        <div className="rounded-lg border hairline p-6">
          <h3 className="font-serif text-xl">Journey CTA interactions</h3>
          {rows === null ? (
            <p className="mt-4 text-sm text-muted-foreground">Loading…</p>
          ) : ctaRows.length === 0 ? (
            <p className="mt-4 text-sm text-muted-foreground">
              No journey events yet in this window.
            </p>
          ) : (
            <ul className="mt-4 divide-y hairline">
              {ctaRows.map(([e, n]) => (
                <li key={e} className="flex items-center justify-between gap-3 py-2">
                  <span className="truncate text-sm text-muted-foreground">{e}</span>
                  <span className="font-mono text-sm text-gold-luminous">{n.toLocaleString()}</span>
                </li>
              ))}
            </ul>
          )}
          <p className="mt-4 text-xs text-muted-foreground">
            Membership and purchase outcomes are counted from existing commerce events only. Revenue
            attribution per journey stage is not measurable and is not shown.
          </p>
        </div>
      </div>
    </AdminSection>
  );
}
